/**
 * collectionReorder — the drag-to-reorder save.
 *
 * Blake's grid moves the card immediately; this module is what makes that move
 * true. It owns the HTTP and nothing else: app.js computes the order, hands it
 * over with an undo, and never learns whether the network exists.
 *
 * ## The endpoint was already right
 *
 *   PATCH /api/collections/reorder  { order: string[] }   (collections.ts:973)
 *
 * Nothing about it needed changing for the web, which is worth stating plainly
 * because the obvious assumption when a surface is missing is that the server
 * is missing too. It is not: the app has driven this route since the "Edit
 * order" modal shipped, and every guarantee below is the route's own.
 *
 * 🪤 THE PAYLOAD IS THE SET. Rows of the caller's that are ABSENT from `order`
 * have their display_order cleared in the same transaction — that is how the
 * route evicts a hidden row squatting on a public slot, and it is deliberate.
 * The consequence for us is severe and easy to miss: sending only the cards
 * the current tab happens to show would silently wipe the arrangement of every
 * item it is hiding. app.js therefore always sends the whole collection, and
 * `assertWholeCollection` below refuses to post anything that looks like a
 * filtered subset rather than trusting that it stays that way.
 *
 * 🪤 `skipped` IS NOT AN ERROR, AND IS NOT SILENCE EITHER. Ids that resolve to
 * nothing come back listed rather than dropped (the pre-2026-07-30 silent skip
 * is how the Highlights save shipped wrong sets while reporting ok). A 200 with
 * a non-empty `skipped` means the save partly landed, and the person is told
 * that rather than told "Order saved".
 *
 * ## Why the undo arrives as an argument
 *
 * The move is optimistic, so a rejected save has to put the grid back. app.js
 * hands `revert` in with the order instead of exposing a second global: the
 * capability to undo travels with the call that might need it, it cannot be
 * invoked out of context, and the demo — which has no server and never calls
 * this module — simply never produces one.
 *
 * A revert can itself refuse (the collection changed shape underneath it). When
 * that happens the message says the order could not be restored rather than
 * claiming it was, because the one thing worse than a failed save is a failed
 * save that reports a clean rollback.
 */

/** Blake's global. app.js calls it; this module is the only thing that sets it. */
export const SAVE_HOOK = "__gbzSaveCollectionOrder";

export const SAVED_MESSAGE = "Order saved";
export const PARTIAL_MESSAGE = "Order saved — some items could not be moved";
export const FAILED_MESSAGE = "Could not save that order — put back";
export const FAILED_NO_REVERT_MESSAGE = "Could not save that order";
export const OFFLINE_MESSAGE = "Could not reach the server — order not saved";

type Revert = (() => boolean) | undefined;
type SaveHook = (order: string[], revert?: Revert) => void;

interface ReorderWindow extends Window {
  __gbzSaveCollectionOrder?: SaveHook;
  /** Blake's toast (app.js). Absent in a bare fixture; never assumed. */
  toast?: (msg: string) => void;
}

/**
 * Everything the caller's collection contains, as far as this module can tell.
 * Read off Blake's own MY_COLLECTION through the bridge rather than recomputed,
 * so "the whole set" means the same thing on both sides of the call.
 */
function knownCollectionSize(win: ReorderWindow): number | null {
  const list = (win as unknown as { MY_COLLECTION?: unknown }).MY_COLLECTION;
  return Array.isArray(list) ? list.length : null;
}

/**
 * Refuse a payload that is not the whole collection.
 *
 * This guard exists because the failure it prevents is silent and destructive:
 * a subset posts 200, and every item missing from it quietly loses its
 * arrangement. There is no error to notice and no screen that looks wrong until
 * someone opens a tab they were not on. Cheap to check, so it is checked on
 * every call rather than argued about once.
 */
export function isWholeCollection(win: ReorderWindow, order: string[]): boolean {
  const size = knownCollectionSize(win);
  if (size === null) return true; // cannot tell — do not block the save
  return order.length === size;
}

function say(win: ReorderWindow, message: string): void {
  if (typeof win.toast === "function") win.toast(message);
}

export function installCollectionReorder(
  win: ReorderWindow,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): void {
  const save: SaveHook = (order, revert) => {
    if (!Array.isArray(order) || order.length === 0) return;

    if (!isWholeCollection(win, order)) {
      // Not posted. A partial payload would clear display_order on everything
      // it omits, so refusing is the conservative half of the trade.
      say(win, FAILED_NO_REVERT_MESSAGE);
      return;
    }

    void (async () => {
      let res: Response;
      try {
        res = await authFetch("/api/collections/reorder", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order }),
        });
      } catch {
        // Network, not rejection. Same user-visible outcome — the order did not
        // save — but named differently, because "we could not reach the server"
        // is actionable and "that order is invalid" is not.
        say(win, revert?.() ? OFFLINE_MESSAGE : FAILED_NO_REVERT_MESSAGE);
        return;
      }

      if (!res.ok) {
        say(win, revert?.() ? FAILED_MESSAGE : FAILED_NO_REVERT_MESSAGE);
        return;
      }

      // A 200 whose body will not parse is still a 200: the write landed. Do
      // not revert a save the server accepted just because the receipt was
      // unreadable — that would invent a failure and undo real work.
      let skipped: unknown = null;
      try {
        skipped = ((await res.json()) as { skipped?: unknown })?.skipped;
      } catch {
        skipped = null;
      }

      const partial = Array.isArray(skipped) && skipped.length > 0;
      say(win, partial ? PARTIAL_MESSAGE : SAVED_MESSAGE);
    })();
  };

  win.__gbzSaveCollectionOrder = save;
}
